/**
 * DocumentBuilder – WYSIWYG document builder for editing Questionnaire items.
 *
 * Renders Questionnaire items as editable blocks matching the document-editing
 * experience. Each block can be edited inline, reordered via DnD, and deleted.
 * New items/groups/pages are added via inline "Add" controls (Medplum
 * QuestionnaireBuilder interaction model).
 *
 * All edits update a local draft of Questionnaire.item[].
 * The parent (TemplateEditorPage) handles persistence on Save.
 *
 * Helpers (generateId, generateLinkId, ensureQuestionnaireKeys, createPage,
 * reorderItems) are aligned with the Medplum QuestionnaireBuilder component
 * to ensure identical Questionnaire data handling.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Radio,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { DateInput, DateTimePicker, TimePicker } from '@mantine/dates';
import {
  IconAlignLeft,
  IconArrowDown,
  IconArrowUp,
  IconCalendar,
  IconCalendarEvent,
  IconClock,
  IconCopy,
  IconGripHorizontal,
  IconLayoutList,
  IconLetterCase,
  IconLink,
  IconList,
  IconListCheck,
  IconMessagePlus,
  IconNumbers,
  IconPaperclip,
  IconPlus,
  IconRuler2,
  IconRoute,
  IconTemplate,
  IconToggleRight,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';

const SELECT_ICON_SIZE = 16;

/** Icon for the question type selector, matching the current type. */
function getIconForQuestionType(type: string): ReactNode {
  switch (type) {
    case 'display':
      return <IconTemplate size={SELECT_ICON_SIZE} />;
    case 'boolean':
      return <IconToggleRight size={SELECT_ICON_SIZE} />;
    case 'decimal':
    case 'integer':
      return <IconNumbers size={SELECT_ICON_SIZE} />;
    case 'date':
      return <IconCalendar size={SELECT_ICON_SIZE} />;
    case 'dateTime':
      return <IconCalendarEvent size={SELECT_ICON_SIZE} />;
    case 'time':
      return <IconClock size={SELECT_ICON_SIZE} />;
    case 'string':
      return <IconLetterCase size={SELECT_ICON_SIZE} />;
    case 'text':
      return <IconAlignLeft size={SELECT_ICON_SIZE} />;
    case 'url':
      return <IconLink size={SELECT_ICON_SIZE} />;
    case 'choice':
      return <IconList size={SELECT_ICON_SIZE} />;
    case 'multi-choice':
      return <IconListCheck size={SELECT_ICON_SIZE} />;
    case 'open-choice':
      return <IconListCheck size={SELECT_ICON_SIZE} />;
    case 'attachment':
      return <IconPaperclip size={SELECT_ICON_SIZE} />;
    case 'quantity':
      return <IconRuler2 size={SELECT_ICON_SIZE} />;
    default:
      return <IconTemplate size={SELECT_ICON_SIZE} />;
  }
}
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import type { QuestionnaireItem, QuestionnaireItemAnswerOption } from '@medplum/fhirtypes';
import { isChoiceQuestion, QUESTIONNAIRE_ITEM_CONTROL_URL } from '@medplum/react-hooks';

import {
  VariableTextInput,
  VariableTextarea,
  VariablePickerPopover,
} from '../../../../lib/template-variables';
import { getAnswerOptionDisplayLabel, patchAnswerOptionDisplayLabel } from '../utils/answerOptionLabel';
import {
  getOptionAddonPlaceholder,
  removeOptionAddon,
  setOptionAddonPlaceholder,
  supportsPerOptionAddons,
  syncOptionAddonEnableWhen,
} from '../utils/choiceOptionAddons';
import {
  clearConditionalRulesReferencing,
  countDependentsOnTrigger,
  createConditionalFollowUpItem,
  isConditionalSiblingItem,
} from '../utils/conditionalQuestions';
import { ConditionalVisibilityGear } from './ConditionalVisibilityEditor';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapUnderline from '@tiptap/extension-underline';
import TiptapLink from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

// ---------------------------------------------------------------------------
// Medplum-aligned ID generation (mirrors QuestionnaireBuilder.tsx)
// ---------------------------------------------------------------------------

let nextLinkId = 1;
let nextId = 1;

/**
 * Generates a link ID for an item.
 * Aligned with Medplum QuestionnaireBuilder's generateLinkId().
 */
export function generateLinkId(prefix: string): string {
  return prefix + nextLinkId++;
}

/**
 * Generates a unique ID for React rendering.
 * Aligned with Medplum QuestionnaireBuilder's generateId().
 */
function generateId(): string {
  return 'id-' + nextId++;
}

// ---------------------------------------------------------------------------
// Medplum-aligned key management
// ---------------------------------------------------------------------------

/**
 * Ensures all items in a Questionnaire item tree have id and linkId.
 * Also advances the sequence counters to avoid collisions.
 * Aligned with Medplum QuestionnaireBuilder's ensureQuestionnaireKeys().
 */
export function ensureQuestionnaireItemKeys(
  items: QuestionnaireItem[] | undefined,
): QuestionnaireItem[] | undefined {
  if (!items) return undefined;

  // First pass: advance counters past any existing IDs
  items.forEach((item) => {
    if (item.id?.match(/^id-\d+$/)) {
      nextId = Math.max(nextId, Number.parseInt(item.id.substring(3), 10) + 1);
    }
    if (item.linkId?.match(/^[a-z]\d+$/)) {
      nextLinkId = Math.max(nextLinkId, Number.parseInt(item.linkId.substring(1), 10) + 1);
    }
  });

  return items.map((item) => ({
    ...item,
    id: item.id || generateId(),
    linkId: item.linkId || generateLinkId('q'),
    item: ensureQuestionnaireItemKeys(item.item),
    answerOption: ensureQuestionnaireOptionKeys(item.answerOption),
  }));
}

function ensureQuestionnaireOptionKeys(
  options: QuestionnaireItemAnswerOption[] | undefined,
): QuestionnaireItemAnswerOption[] | undefined {
  if (!options) return undefined;
  return options.map((option) => ({
    ...option,
    id: option.id || generateId(),
  }));
}

function linkIdPrefixForItem(item: QuestionnaireItem): string {
  const control = item.extension?.find(
    (e) => e.url === QUESTIONNAIRE_ITEM_CONTROL_URL,
  )?.valueCodeableConcept?.coding?.[0]?.code;
  if (control === 'page') return 's';
  if (item.type === 'group') return 'g';
  return 'q';
}

function regenerateItemKeysForDuplicate(item: QuestionnaireItem): QuestionnaireItem {
  return {
    ...item,
    id: generateId(),
    linkId: generateLinkId(linkIdPrefixForItem(item)),
    item: item.item?.map(regenerateItemKeysForDuplicate),
    answerOption: item.answerOption?.map((option) => ({
      ...option,
      id: generateId(),
    })),
  };
}

/**
 * Deep-clone a questionnaire item with new id/linkId (for duplicate).
 */
function cloneItemForDuplicate(item: QuestionnaireItem): QuestionnaireItem {
  return regenerateItemKeysForDuplicate(JSON.parse(JSON.stringify(item)) as QuestionnaireItem);
}

function removeSiblingWithConditionalCleanup(
  siblings: QuestionnaireItem[],
  index: number,
): { next: QuestionnaireItem[]; clearedCount: number } {
  const removed = siblings[index];
  const triggerLinkId = removed?.linkId;
  let next = siblings.filter((_, i) => i !== index);
  if (!triggerLinkId) {
    return { next, clearedCount: 0 };
  }
  const clearedCount = countDependentsOnTrigger(siblings, triggerLinkId);
  next = clearConditionalRulesReferencing(next, triggerLinkId);
  return { next, clearedCount };
}

function notifyConditionalRulesCleared(clearedCount: number): void {
  if (clearedCount <= 0) return;
  notifications.show({
    title: 'Visibility rules updated',
    message:
      clearedCount === 1
        ? 'Cleared conditional visibility on 1 follow-up question.'
        : `Cleared conditional visibility on ${clearedCount} follow-up questions.`,
    color: 'yellow',
    autoClose: 5000,
  });
}

// ---------------------------------------------------------------------------
// Medplum-aligned page creation
// ---------------------------------------------------------------------------

/**
 * Creates a new Page item (group with page extension).
 * Aligned with Medplum QuestionnaireBuilder's createPage().
 */
export function createPage(): QuestionnaireItem {
  return {
    id: generateId(),
    linkId: generateLinkId('s'),
    type: 'group',
    text: 'New Page',
    extension: [
      {
        url: QUESTIONNAIRE_ITEM_CONTROL_URL,
        valueCodeableConcept: {
          coding: [
            {
              system: 'http://hl7.org/fhir/questionnaire-item-control',
              code: 'page',
            },
          ],
        },
      },
    ],
  } as QuestionnaireItem;
}

/**
 * Reorder items by swapping at index with index+delta.
 * Aligned with Medplum QuestionnaireBuilder's reorderItems().
 */
function reorderItems(
  items: QuestionnaireItem[] | undefined,
  itemIndex: number,
  delta: number,
): QuestionnaireItem[] {
  const currentItems = items ?? [];
  const newIndex = itemIndex + delta;
  if (newIndex < 0 || newIndex >= currentItems.length) return currentItems;
  const updatedItems = [...currentItems];
  [updatedItems[itemIndex], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[itemIndex]];
  return updatedItems;
}

// ---------------------------------------------------------------------------
// Constants & helpers (our styling/DnD layer)
// ---------------------------------------------------------------------------

const ITEM_CONTROL_URL =
  'http://hl7.org/fhir/StructureDefinition/questionnaire-itemControl';
const ITEM_CONTROL_SYSTEM =
  'http://hl7.org/fhir/questionnaire-item-control';

const DRAG_TYPE = 'TEMPLATE_BLOCK';

/** Edge distance (px) from top/bottom of scroll container to trigger scroll */
const DRAG_SCROLL_EDGE = 80;
/** Pixels to scroll per frame when cursor is at the edge */
const DRAG_SCROLL_SPEED = 14;

/** Sentinel for "form header" selection (title + description block). */
export const FORM_HEADER_SELECTION = 'form-header';

/** Returns true if any item in the tree has required === true */
function hasRequiredItem(items: QuestionnaireItem[] | undefined): boolean {
  if (!items?.length) return false;
  return items.some((item) => !!item.required || hasRequiredItem(item.item));
}

/** Inputs inside cards always use white background so they don’t blend when card is gray. */
const INNER_INPUT_BG = { backgroundColor: 'var(--mantine-color-white)' };

interface DragItem {
  linkId: string;
  parentLinkId: string | null;
  index: number;
  isPage: boolean;
}

function getItemControl(item: QuestionnaireItem): string | undefined {
  return item.extension?.find(
    (e) => e.url === ITEM_CONTROL_URL,
  )?.valueCodeableConcept?.coding?.[0]?.code;
}

function isPageItem(item: QuestionnaireItem): boolean {
  return item.type === 'group' && getItemControl(item) === 'page';
}

function findItemInTree(items: QuestionnaireItem[], linkId: string): QuestionnaireItem | undefined {
  for (const it of items) {
    if (it.linkId === linkId) return it;
    if (it.type === 'group' && it.item?.length) {
      const found = findItemInTree(it.item, linkId);
      if (found) return found;
    }
  }
  return undefined;
}

/** Path of indices from root to the item with linkId (e.g. [0, 1] = items[0].item[1]). */
function findPathToItem(
  items: QuestionnaireItem[],
  linkId: string,
  path: number[] = [],
): number[] | null {
  for (let i = 0; i < items.length; i++) {
    if (items[i].linkId === linkId) return [...path, i];
    if (items[i].type === 'group' && items[i].item?.length) {
      const found = findPathToItem(items[i].item!, linkId, [...path, i]);
      if (found) return found;
    }
  }
  return null;
}

/** Immutably update a group's item array (finds group at any depth). */
function updateGroupItems(
  items: QuestionnaireItem[],
  groupLinkId: string,
  updater: (children: QuestionnaireItem[]) => QuestionnaireItem[],
): QuestionnaireItem[] {
  return items.map((itm) => {
    if (itm.linkId === groupLinkId) {
      const next = updater(itm.item ?? []);
      return { ...itm, item: next };
    }
    if (itm.type === 'group' && itm.item?.length) {
      return { ...itm, item: updateGroupItems(itm.item, groupLinkId, updater) };
    }
    return itm;
  });
}

function makeItemControlExtension(code: string) {
  return [
    {
      url: ITEM_CONTROL_URL,
      valueCodeableConcept: {
        coding: [{ system: ITEM_CONTROL_SYSTEM, code }],
      },
    },
  ];
}

/** Multi-select presentation modes (questionnaire-itemControl). */
const MULTI_CHOICE_LAYOUTS = [
  { value: 'check-box', label: 'Single column' },
  { value: 'check-box-grid', label: 'Multi-column' },
] as const;

function getMultiChoiceLayout(item: QuestionnaireItem): string {
  // Default multi-choice presentation is the 3-column grid; only explicit `check-box` is single-column.
  return getItemControl(item) === 'check-box' ? 'check-box' : 'check-box-grid';
}

/** Single-select presentation modes — `radio` is the bare default (no itemControl extension). */
const SINGLE_CHOICE_LAYOUTS = [
  { value: 'radio', label: 'Radio buttons' },
  { value: 'drop-down', label: 'Dropdown' },
] as const;

function getSingleChoiceLayout(item: QuestionnaireItem): string {
  return getItemControl(item) === 'drop-down' ? 'drop-down' : 'radio';
}

/** Radio (default) clears the itemControl extension; dropdown sets `drop-down`. */
function singleChoiceLayoutExtensions(
  item: QuestionnaireItem,
  layout: string,
): QuestionnaireItem['extension'] {
  if (layout === 'drop-down') return withItemControl(item, 'drop-down');
  return (item.extension ?? []).filter((e) => e.url !== ITEM_CONTROL_URL);
}

/** Replace the itemControl extension, preserving any other extensions on the item. */
function withItemControl(item: QuestionnaireItem, code: string): QuestionnaireItem['extension'] {
  return [
    ...(item.extension ?? []).filter((e) => e.url !== ITEM_CONTROL_URL),
    ...makeItemControlExtension(code),
  ];
}

/** Keep the itemControl consistent when switching a choice item between single- and multi-select. */
function resolveChoiceExtensions(
  item: QuestionnaireItem,
  isMultiChoice: boolean,
): QuestionnaireItem['extension'] {
  if (isMultiChoice) return withItemControl(item, getMultiChoiceLayout(item));
  const control = getItemControl(item);
  if (control !== 'check-box' && control !== 'check-box-grid') return item.extension;
  return (item.extension ?? []).filter((e) => e.url !== ITEM_CONTROL_URL);
}

function isHeading(item: QuestionnaireItem): boolean {
  return item.type === 'display' && getItemControl(item) === 'header';
}

function isDividerItem(item: QuestionnaireItem): boolean {
  return item.type === 'display' && getItemControl(item) === 'separator';
}

function isDropdown(item: QuestionnaireItem): boolean {
  return (
    (item.type === 'choice' || item.type === 'open-choice') &&
    getItemControl(item) === 'drop-down'
  );
}

function isRichTextBlock(item: QuestionnaireItem): boolean {
  return item.type === 'text' && getItemControl(item) === 'rich-text';
}

// ---------------------------------------------------------------------------
// Question type definitions (Medplum builder types + our content blocks)
// ---------------------------------------------------------------------------

/** All supported question types, matching Medplum QuestionnaireBuilder's type list. */
const QUESTION_TYPES = [
  { value: 'display', label: 'Display' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'integer', label: 'Integer' },
  { value: 'date', label: 'Date' },
  { value: 'dateTime', label: 'Date/Time' },
  { value: 'time', label: 'Time' },
  { value: 'string', label: 'Short Text' },
  { value: 'text', label: 'Long Text' },
  { value: 'url', label: 'URL' },
  { value: 'choice', label: 'Single Choice' },
  { value: 'multi-choice', label: 'Multi Choice' },
  { value: 'open-choice', label: 'Open Choice' },
  { value: 'attachment', label: 'Attachment' },
  { value: 'quantity', label: 'Quantity' },
] as const;

/**
 * Derives the "effective type" for display in the type selector.
 * Maps FHIR `choice` + `repeats: true` → our virtual `multi-choice` label.
 */
function getEffectiveType(item: QuestionnaireItem): string {
  if (item.type === 'choice' && item.repeats) return 'multi-choice';
  return item.type;
}

/** FHIR que-6: display items must not carry required or repeats (or answer fields). */
function sanitizeQuestionnaireItem(item: QuestionnaireItem): QuestionnaireItem {
  if (item.type !== 'display') return item;
  const next = { ...item };
  delete next.required;
  delete next.repeats;
  delete next.answerOption;
  delete next.answerValueSet;
  delete next.enableWhen;
  delete next.enableBehavior;
  return next;
}

/** Strip invalid fields from display items throughout the item tree (e.g. before save). */
export function sanitizeQuestionnaireItemTree(
  items: QuestionnaireItem[] | undefined,
): QuestionnaireItem[] | undefined {
  if (!items?.length) return items;
  return items.map((item) => {
    const next = sanitizeQuestionnaireItem(item);
    if (next.item?.length) {
      return { ...next, item: sanitizeQuestionnaireItemTree(next.item) };
    }
    return next;
  });
}

function applyQuestionnaireItemUpdate(
  item: QuestionnaireItem,
  update: Partial<QuestionnaireItem>,
): QuestionnaireItem {
  return sanitizeQuestionnaireItem({ ...item, ...update } as QuestionnaireItem);
}

/**
 * Safely convert an item to a new type, preserving common fields and
 * resetting incompatible ones. Aligned with Medplum builder's type model.
 */
function convertItemType(
  item: QuestionnaireItem,
  newType: string,
): QuestionnaireItem {
  // Resolve virtual type: multi-choice → choice with repeats
  const isMultiChoice = newType === 'multi-choice';
  const resolvedNewType = isMultiChoice ? 'choice' : newType;
  const oldEffective = getEffectiveType(item);
  if (oldEffective === newType) return item;

  const oldType = item.type;

  // Always preserved
  const base: Partial<QuestionnaireItem> = {
    id: item.id,
    linkId: item.linkId,
    text: item.text,
    ...(resolvedNewType !== 'display' && resolvedNewType !== 'group'
      ? { enableWhen: item.enableWhen, enableBehavior: item.enableBehavior }
      : {}),
  };

  const isOldChoice = oldType === 'choice' || oldType === 'open-choice';
  const isNewChoice = resolvedNewType === 'choice' || resolvedNewType === 'open-choice';

  // Choice → Choice (including multi-choice): keep answer options, set repeats from target type only
  if (isOldChoice && isNewChoice) {
    return {
      ...base,
      type: resolvedNewType,
      answerOption: item.answerOption,
      answerValueSet: item.answerValueSet,
      repeats: isMultiChoice ? true : undefined,
      required: item.required,
      extension: resolveChoiceExtensions(item, isMultiChoice),
    } as QuestionnaireItem;
  }

  // Non-choice → Choice: seed default options
  if (!isOldChoice && isNewChoice) {
    return {
      ...base,
      type: resolvedNewType,
      required: item.required,
      repeats: isMultiChoice ? true : undefined,
      extension: isMultiChoice
        ? withItemControl(item, 'check-box-grid')
        : (item.extension ?? []).filter((e) => e.url !== ITEM_CONTROL_URL),
      answerOption: [
        { id: generateId(), valueString: 'Option 1' },
        { id: generateId(), valueString: 'Option 2' },
      ],
    } as QuestionnaireItem;
  }

  // Choice → Non-choice: drop answer fields
  if (isOldChoice && !isNewChoice) {
    return {
      ...base,
      type: resolvedNewType,
      required: resolvedNewType !== 'display' && resolvedNewType !== 'group' ? item.required : undefined,
    } as QuestionnaireItem;
  }

  // Any → Group: reset to container
  if (resolvedNewType === 'group') {
    return {
      ...base,
      type: 'group',
      item: [],
    } as QuestionnaireItem;
  }

  // Group → Non-group: drop children
  if (oldType === 'group') {
    return {
      ...base,
      type: resolvedNewType,
      required: resolvedNewType !== 'display' ? item.required : undefined,
    } as QuestionnaireItem;
  }

  // Any → Display: drop answer/required fields (sanitize also strips stale keys on merge)
  if (resolvedNewType === 'display') {
    return sanitizeQuestionnaireItem({
      ...base,
      type: 'display',
      extension: item.extension,
    } as QuestionnaireItem);
  }

  // Default: simple type change, preserve required + repeats
  return {
    ...base,
    type: resolvedNewType,
    required: item.required,
    repeats: item.repeats,
  } as QuestionnaireItem;
}

// ---------------------------------------------------------------------------
// Item creation (Medplum builder-style)
// ---------------------------------------------------------------------------

/**
 * Create a new QuestionnaireItem for a question.
 * Uses Medplum-aligned ID generation (id + linkId).
 */
function createQuestionItem(): QuestionnaireItem {
  return {
    id: generateId(),
    linkId: generateLinkId('q'),
    type: 'string',
    text: 'Question',
  } as QuestionnaireItem;
}

/**
 * Create a new QuestionnaireItem from a block type identifier.
 * Used by the sidebar palette (legacy, kept for starter content creation).
 */
export function createQuestionnaireItem(
  blockType: string,
): QuestionnaireItem {
  const id = generateId();
  const linkId = generateLinkId('q');

  switch (blockType) {
    case 'short-response':
      return { id, linkId, type: 'string', text: 'Short response question' } as QuestionnaireItem;
    case 'long-response':
      return { id, linkId, type: 'text', text: 'Long response question' } as QuestionnaireItem;
    case 'single-option':
      return {
        id, linkId, type: 'choice', text: 'Single option question',
        answerOption: [
          { id: generateId(), valueString: 'Option 1' },
          { id: generateId(), valueString: 'Option 2' },
        ],
      } as QuestionnaireItem;
    case 'multiple-options':
      return {
        id, linkId, type: 'choice', text: 'Multiple option question', repeats: true,
        extension: makeItemControlExtension('check-box-grid'),
        answerOption: [
          { id: generateId(), valueString: 'Option 1' },
          { id: generateId(), valueString: 'Option 2' },
        ],
      } as QuestionnaireItem;
    case 'dropdown':
      return {
        id, linkId, type: 'choice', text: 'Dropdown question',
        extension: makeItemControlExtension('drop-down'),
        answerOption: [
          { id: generateId(), valueString: 'Option 1' },
          { id: generateId(), valueString: 'Option 2' },
        ],
      } as QuestionnaireItem;
    case 'date':
      return { id, linkId, type: 'date', text: 'Date question' } as QuestionnaireItem;
    case 'signature':
      return { id, linkId, type: 'string', text: 'Signature' } as QuestionnaireItem;
    case 'group':
      return { id, linkId: generateLinkId('g'), type: 'group', text: 'New section', item: [] } as QuestionnaireItem;
    case 'heading':
      return {
        id, linkId, type: 'display', text: 'Section heading',
        extension: makeItemControlExtension('header'),
      } as QuestionnaireItem;
    case 'description':
      return { id, linkId, type: 'display', text: 'Description text' } as QuestionnaireItem;
    case 'rich-text-block':
      return {
        id, linkId, type: 'text', text: 'Rich Text Block',
        extension: makeItemControlExtension('rich-text'),
        initial: [{ valueString: '<p>Enter content here…</p>' }],
      } as QuestionnaireItem;
    case 'divider':
      return {
        id, linkId, type: 'display', text: '',
        extension: makeItemControlExtension('separator'),
      } as QuestionnaireItem;
    default:
      return { id, linkId, type: 'string', text: 'New question' } as QuestionnaireItem;
  }
}

/**
 * Starter template items seeded into empty templates so the editor
 * is immediately meaningful. Draft-only — not persisted unless the user saves.
 */
export const STARTER_TEMPLATE_ITEMS: QuestionnaireItem[] = [
  {
    id: generateId(),
    linkId: generateLinkId('q'),
    type: 'display',
    text: 'Section title',
    extension: makeItemControlExtension('header'),
  } as QuestionnaireItem,
  {
    id: generateId(),
    linkId: generateLinkId('q'),
    type: 'display',
    text: 'Add instructions or context for this section here.',
  } as QuestionnaireItem,
  {
    id: generateId(),
    linkId: generateLinkId('q'),
    type: 'choice',
    text: 'How would you rate your experience?',
    answerOption: [
      { id: generateId(), valueString: 'Excellent' },
      { id: generateId(), valueString: 'Good' },
      { id: generateId(), valueString: 'Fair' },
      { id: generateId(), valueString: 'Poor' },
    ],
  } as QuestionnaireItem,
  {
    id: generateId(),
    linkId: generateLinkId('q'),
    type: 'string',
    text: 'What is your primary concern?',
  } as QuestionnaireItem,
  {
    id: generateId(),
    linkId: generateLinkId('q'),
    type: 'text',
    text: 'Please provide any additional details',
  } as QuestionnaireItem,
];

// ---------------------------------------------------------------------------
// Scroll while dragging (so user can move sections beyond viewport)
// ---------------------------------------------------------------------------

interface ScrollWhileDraggingProps {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

function ScrollWhileDragging({ scrollContainerRef }: ScrollWhileDraggingProps): null {
  const { isDragging, itemType, clientOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    itemType: monitor.getItemType(),
    clientOffset: monitor.getClientOffset(),
  }));
  const latestOffsetRef = useRef(clientOffset);
  latestOffsetRef.current = clientOffset;

  useEffect(() => {
    if (!isDragging || itemType !== DRAG_TYPE || !scrollContainerRef.current) return;
    let rafId: number;
    const tick = (): void => {
      const el = scrollContainerRef.current;
      const offset = latestOffsetRef.current;
      if (!el || !offset) {
        rafId = requestAnimationFrame(tick);
        return;
      }
      const rect = el.getBoundingClientRect();
      const y = offset.y;
      if (y <= rect.top + DRAG_SCROLL_EDGE) {
        el.scrollTop = Math.max(0, el.scrollTop - DRAG_SCROLL_SPEED);
      } else if (y >= rect.bottom - DRAG_SCROLL_EDGE) {
        el.scrollTop = Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + DRAG_SCROLL_SPEED);
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isDragging, itemType, scrollContainerRef]);

  return null;
}

// ---------------------------------------------------------------------------
// BlockFooterActions – add controls at bottom of each block (replaces hovering sidebar)
// ---------------------------------------------------------------------------

interface BlockFooterActionsProps {
  parentLinkId: string | null;
  parentIsPage: boolean;
  index: number;
  onInsertAfter: (parentLinkId: string | null, index: number, newItem: QuestionnaireItem) => void;
}

function BlockFooterActions({
  parentLinkId,
  parentIsPage,
  index,
  onInsertAfter,
}: BlockFooterActionsProps): JSX.Element {
  const questionLabel = parentIsPage ? 'Insert question' : 'Add question';
  return (
    <Group gap="xs" wrap="nowrap" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <Button
        variant="subtle"
        size="compact-xs"
        leftSection={<IconPlus size={12} />}
        onClick={() => onInsertAfter(parentLinkId, index, createQuestionItem())}
      >
        {questionLabel}
      </Button>
      {!parentIsPage && (
        <Button
          variant="subtle"
          size="compact-xs"
          leftSection={<IconLayoutList size={12} />}
          onClick={() => onInsertAfter(parentLinkId, index, createPage())}
        >
          Add page
        </Button>
      )}
    </Group>
  );
}

// ---------------------------------------------------------------------------
// DocumentBuilder (main export)
// ---------------------------------------------------------------------------

/** Selection context: which element is selected and insert helpers */
interface SelectionContext {
  selectedLinkId: string | null;
  onSelect: (linkId: string) => void;
  onInsertAfter: (parentLinkId: string | null, index: number, newItem: QuestionnaireItem) => void;
}

/** Optional treatment-plan diagnosis: shown under title when category is Treatment Planning. */
export interface TreatmentPlanDiagnosisProps {
  code: string;
  onCodeChange: (code: string) => void;
  description: string;
  options: { code: string; description: string }[];
}

interface DocumentBuilderProps {
  title: string;
  onTitleChange: (title: string) => void;
  /** Optional form description (Google Forms–style). */
  description?: string;
  onDescriptionChange?: (description: string) => void;
  items: QuestionnaireItem[];
  onItemsChange: (items: QuestionnaireItem[]) => void;
  /** Optional ref to the scrollable container so we can scroll while dragging. */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** When set, renders diagnosis code + description block under the title (Treatment plans). */
  treatmentPlanDiagnosis?: TreatmentPlanDiagnosisProps;
  /** Initial selection state (e.g. FORM_HEADER_SELECTION for new templates). */
  initialSelectedLinkId?: string | null;
}

export function DocumentBuilder({
  title,
  onTitleChange,
  description = '',
  onDescriptionChange,
  items,
  onItemsChange,
  scrollContainerRef,
  treatmentPlanDiagnosis,
  initialSelectedLinkId = null,
}: DocumentBuilderProps): JSX.Element {
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(initialSelectedLinkId);

  const updateItem = useCallback(
    (index: number, update: Partial<QuestionnaireItem>) => {
      onItemsChange(
        items.map((item, i) =>
          i === index ? applyQuestionnaireItemUpdate(item, update) : item,
        ),
      );
    },
    [items, onItemsChange],
  );

  const removeItem = useCallback(
    (index: number) => {
      const { next, clearedCount } = removeSiblingWithConditionalCleanup(items, index);
      onItemsChange(next);
      notifyConditionalRulesCleared(clearedCount);
    },
    [items, onItemsChange],
  );

  const moveItem = useCallback(
    (index: number, direction: 'up' | 'down') => {
      const delta = direction === 'up' ? -1 : 1;
      onItemsChange(reorderItems(items, index, delta));
    },
    [items, onItemsChange],
  );

  const duplicateItem = useCallback(
    (index: number) => {
      const clone = cloneItemForDuplicate(items[index]);
      const next = [...items.slice(0, index + 1), clone, ...items.slice(index + 1)];
      onItemsChange(next);
    },
    [items, onItemsChange],
  );

  const handleMoveBlock = useCallback(
    (draggedLinkId: string, targetParentLinkId: string | null, targetIndex: number) => {
      if (targetParentLinkId === draggedLinkId) return;

      const draggedItem = findItemInTree(items, draggedLinkId);
      if (draggedItem && isPageItem(draggedItem) && targetParentLinkId !== null) {
        const targetParent = findItemInTree(items, targetParentLinkId);
        if (targetParent && isPageItem(targetParent)) return;
      }

      const sourcePath = findPathToItem(items, draggedLinkId);
      if (!sourcePath || sourcePath.length === 0) return;

      const sourceIndex = sourcePath[sourcePath.length - 1];
      const sourceParentPath = sourcePath.slice(0, -1);

      const targetGroupPath = targetParentLinkId === null ? null : findPathToItem(items, targetParentLinkId);
      if (targetParentLinkId !== null && (!targetGroupPath || targetGroupPath.length === 0)) return;

      const sameParent =
        targetParentLinkId === null
          ? sourcePath.length === 1
          : !!targetGroupPath &&
          sourceParentPath.length === targetGroupPath.length &&
          sourceParentPath.every((v, i) => v === targetGroupPath[i]);
      if (sameParent && (sourceIndex === targetIndex || sourceIndex + 1 === targetIndex)) return;

      const newItems = JSON.parse(JSON.stringify(items)) as QuestionnaireItem[];

      let sourceArr: QuestionnaireItem[] = newItems;
      for (let i = 0; i < sourceParentPath.length; i++) {
        const parentItem = sourceArr[sourceParentPath[i]];
        if (!parentItem?.item) return;
        sourceArr = parentItem.item;
      }
      const [removed] = sourceArr.splice(sourceIndex, 1);

      // Re-resolve target path after removal so indices are correct (e.g. moving root item into page shifts page index)
      const resolvedTargetPath =
        targetParentLinkId === null ? null : findPathToItem(newItems, targetParentLinkId);
      if (targetParentLinkId !== null && (!resolvedTargetPath || resolvedTargetPath.length === 0))
        return;

      let adj = targetIndex;
      if (sameParent && sourceIndex < targetIndex) adj--;

      let targetArr: QuestionnaireItem[];
      if (targetParentLinkId === null) {
        targetArr = newItems;
      } else {
        let arr: QuestionnaireItem[] = newItems;
        let groupItem: QuestionnaireItem | undefined;
        for (let i = 0; i < resolvedTargetPath!.length; i++) {
          groupItem = arr[resolvedTargetPath![i]];
          if (groupItem == null) return;
          if (i < resolvedTargetPath!.length - 1) {
            if (!groupItem.item) return;
            arr = groupItem.item;
          }
        }
        if (!groupItem!.item) groupItem!.item = [];
        targetArr = groupItem!.item;
      }
      targetArr.splice(adj, 0, removed);

      onItemsChange(newItems);
    },
    [items, onItemsChange],
  );

  /** Insert a new item after a given index within a parent scope. Selects the new item. */
  const handleInsertAfter = useCallback(
    (parentLinkId: string | null, afterIndex: number, newItem: QuestionnaireItem) => {
      if (parentLinkId === null) {
        const next = [...items.slice(0, afterIndex + 1), newItem, ...items.slice(afterIndex + 1)];
        onItemsChange(next);
      } else {
        const newItems = updateGroupItems(items, parentLinkId, (children) => {
          const next = [...children];
          next.splice(afterIndex + 1, 0, newItem);
          return next;
        });
        onItemsChange(newItems);
      }
      setSelectedLinkId(newItem.linkId);
    },
    [items, onItemsChange],
  );

  const handleAddItem = useCallback(() => {
    const newItem = createQuestionItem();
    onItemsChange([...items, newItem]);
    setSelectedLinkId(newItem.linkId);
  }, [items, onItemsChange]);

  const handleAddRichTextBlock = useCallback(() => {
    const newItem = createQuestionnaireItem('rich-text-block');
    onItemsChange([...items, newItem]);
    setSelectedLinkId(newItem.linkId);
  }, [items, onItemsChange]);

  const handleAddPage = useCallback(() => {
    const newPage = createPage();
    onItemsChange([...items, newPage]);
    setSelectedLinkId(newPage.linkId);
  }, [items, onItemsChange]);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setSelectedLinkId(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const selectionCtx: SelectionContext = {
    selectedLinkId,
    onSelect: setSelectedLinkId,
    onInsertAfter: handleInsertAfter,
  };

  const isHeaderSelected = selectedLinkId === FORM_HEADER_SELECTION;

  return (
    <DndProvider backend={HTML5Backend}>
      {scrollContainerRef && <ScrollWhileDragging scrollContainerRef={scrollContainerRef} />}
      <Stack>
        {/* Form header: gold bar at top (Google Forms style), selectable; when selected show editable inputs, when not show preview-style */}
        <Paper
          withBorder
          radius="md"
          style={{
            backgroundColor: 'var(--mantine-color-white)',
            borderLeft: isHeaderSelected ? '3px solid var(--mantine-color-gold-5)' : undefined,
            cursor: 'pointer',
            transition: 'border-color 150ms',
            overflow: 'hidden',
          }}
          onClick={() => setSelectedLinkId(FORM_HEADER_SELECTION)}
        >
          {/* Gold accent bar at top (Google Forms style) */}
          <Box
            style={{
              height: 12,
              backgroundColor: 'var(--mantine-color-gold-5)',
              width: '100%',
            }}
          />
          <Group justify="space-between" align="flex-start" wrap="nowrap" p="lg" gap="md">
            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              {isHeaderSelected ? (
                <>
                  <VariableTextInput
                    value={title}
                    onChange={onTitleChange}
                    deferredCommit
                    variant="unstyled"
                    size="xl"
                    styles={{
                      input: {
                        fontSize: '1.75rem',
                        fontWeight: 400,
                        paddingLeft: 'var(--mantine-spacing-md)',
                        paddingRight: 'var(--mantine-spacing-md)',
                      },
                    }}
                    placeholder="Form title"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {onDescriptionChange && (
                    <VariableTextInput
                      value={description}
                      onChange={onDescriptionChange}
                      deferredCommit
                      variant="unstyled"
                      placeholder="Form description"
                      styles={{
                        input: {
                          fontSize: 'var(--mantine-font-size-sm)',
                          color: 'var(--mantine-color-gray-6)',
                          paddingLeft: 'var(--mantine-spacing-md)',
                          paddingRight: 'var(--mantine-spacing-md)',
                          marginTop: 'var(--mantine-spacing-xs)',
                        },
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </>
              ) : (
                <>
                  <Text c="dark" style={{ fontSize: '1.75rem', lineHeight: 1.3 }}>
                    {title || 'Form title'}
                  </Text>
                  {onDescriptionChange && description && (
                    <Text size="sm" c="dimmed" mt="xs">
                      {description}
                    </Text>
                  )}
                </>
              )}
            </Stack>
            {hasRequiredItem(items) && (
              <Text size="xs" c="red" style={{ flexShrink: 0 }}>
                * indicates a required field
              </Text>
            )}
          </Group>
        </Paper>

        {/* Treatment plan: default diagnosis (under title). Dropdown shows code – description; selected display shows code only. */}
        {treatmentPlanDiagnosis && (
          <Paper withBorder p="md" radius="sm" mt="md">
            <Group align="flex-start" gap="md" wrap="wrap">
              <Select
                label="Diagnosis code"
                placeholder="Select ICD-10 code"
                data={treatmentPlanDiagnosis.options.map((o) => ({
                  value: o.code,
                  label: o.code,
                  description: o.description,
                }))}
                value={treatmentPlanDiagnosis.code || null}
                onChange={(v) => treatmentPlanDiagnosis.onCodeChange(v ?? '')}
                clearable
                searchable
                limit={12}
                filter={({ options, search }) => {
                  const s = search.toLowerCase().trim();
                  if (!s) return options;
                  return options.filter((opt) => {
                    const item = opt as { value?: string; label?: string; description?: string };
                    return (
                      String(item.label ?? '').toLowerCase().includes(s) ||
                      String(item.description ?? '').toLowerCase().includes(s)
                    );
                  });
                }}
                renderOption={({ option }) => {
                  const item = option as { value?: string; description?: string };
                  const fullLabel =
                    item.value && item.description
                      ? `${item.value} – ${item.description}`
                      : String(option.label ?? option.value);
                  return (
                    <Text
                      size="sm"
                      style={{
                        maxWidth: 380,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={fullLabel}
                    >
                      {fullLabel}
                    </Text>
                  );
                }}
                style={{ minWidth: 160 }}
              />
              <TextInput
                label="Diagnosis description"
                value={treatmentPlanDiagnosis.description}
                readOnly
                placeholder="Select a code to see description"
                style={{ flex: 1, minWidth: 240 }}
                styles={{ input: { backgroundColor: 'var(--mantine-color-gray-0)' } }}
              />
            </Group>
          </Paper>
        )}

        {/* Render items */}
        {items.map((item, index) => (
          <EditableBlock
            key={item.id || item.linkId}
            item={item}
            index={index}
            total={items.length}
            parentLinkId={null}
            parentIsPage={false}
            siblingItems={items}
            onUpdate={(update) => updateItem(index, update)}
            onDuplicate={() => duplicateItem(index)}
            onDelete={() => removeItem(index)}
            onMoveUp={() => moveItem(index, 'up')}
            onMoveDown={() => moveItem(index, 'down')}
            onMoveBlock={handleMoveBlock}
            selection={selectionCtx}
          />
        ))}

        {/* Bottom add: Add Question, Rich Text Block, and Add Page */}
        <Group gap="sm">
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddItem}
          >
            Add Question
          </Button>
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconAlignLeft size={14} />}
            onClick={handleAddRichTextBlock}
          >
            Rich Text Block
          </Button>
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconLayoutList size={14} />}
            onClick={handleAddPage}
          >
            Add Page
          </Button>
        </Group>
      </Stack>
    </DndProvider>
  );
}

// ---------------------------------------------------------------------------
// EditableBlock – wrapper with hover controls
// ---------------------------------------------------------------------------

interface EditableBlockProps {
  item: QuestionnaireItem;
  index: number;
  total: number;
  parentLinkId: string | null;
  /** True when this block is inside a page (group with page control). Used to disallow nesting pages. */
  parentIsPage?: boolean;
  /** Sibling items in the same section (for conditional visibility). */
  siblingItems: QuestionnaireItem[];
  onUpdate: (update: Partial<QuestionnaireItem>) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onMoveBlock: (linkId: string, parentLinkId: string | null, index: number) => void;
  /** Selection context for sidebar */
  selection?: SelectionContext;
}

function EditableBlock({
  item,
  index,
  total,
  parentLinkId,
  parentIsPage = false,
  siblingItems,
  onUpdate,
  onDuplicate,
  onDelete,
  onMoveUp,
  onMoveDown,
  onMoveBlock,
  selection,
}: EditableBlockProps): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(null);
  const blockRef = useRef<HTMLDivElement | null>(null);
  const isSelected = selection?.selectedLinkId === item.linkId;

  const [{ isDragging }, drag, dragPreview] = useDrag<DragItem, void, { isDragging: boolean }>({
    type: DRAG_TYPE,
    item: {
      linkId: item.linkId,
      parentLinkId,
      index,
      isPage: isPageItem(item),
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const isPage = isPageItem(item);
  const PAGE_EDGE = 0.25;
  const [{ isOver, canDrop }, drop] = useDrop<DragItem, void, { isOver: boolean; canDrop: boolean }>({
    accept: DRAG_TYPE,
    hover: (dragItem, monitor) => {
      if (!blockRef.current) { setDropPosition(null); return; }

      const shallowOver = monitor.isOver({ shallow: true });
      const anyOver = monitor.isOver();

      if (!shallowOver && !isPage) { setDropPosition(null); return; }
      if (!anyOver) { setDropPosition(null); return; }
      if (dragItem.linkId === item.linkId) { setDropPosition(null); return; }

      const rect = blockRef.current.getBoundingClientRect();
      const clientY = monitor.getClientOffset()?.y ?? 0;

      if (isPage && !shallowOver) {
        const yFrac = (clientY - rect.top) / rect.height;
        if (yFrac < PAGE_EDGE) {
          setDropPosition('before');
        } else if (yFrac > 1 - PAGE_EDGE) {
          setDropPosition('after');
        } else {
          setDropPosition(null);
        }
      } else {
        setDropPosition(clientY - rect.top < rect.height / 2 ? 'before' : 'after');
      }
    },
    drop: (dragItem, monitor) => {
      if (monitor.didDrop() || !dropPosition || dragItem.linkId === item.linkId) return;
      onMoveBlock(dragItem.linkId, parentLinkId, dropPosition === 'before' ? index : index + 1);
      setDropPosition(null);
    },
    canDrop: (dragItem) =>
      dragItem.linkId !== item.linkId && !(dragItem.isPage && parentIsPage),
    collect: (monitor) => ({
      isOver: isPage ? monitor.isOver() : monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  });

  useEffect(() => { if (!isOver) setDropPosition(null); }, [isOver]);

  const attachRef = useCallback(
    (node: HTMLDivElement | null) => {
      blockRef.current = node;
      drop(dragPreview(node));
    },
    [drop, dragPreview],
  );

  const isRichText = isRichTextBlock(item);
  const showRequiredToggle = item.type !== 'display' && item.type !== 'group' && !isRichText;
  const showDropBefore = isOver && canDrop && dropPosition === 'before';
  const showDropAfter = isOver && canDrop && dropPosition === 'after';
  /** Only show extra entry fields and actions when this element is selected (Google Forms style). */
  const showExtraFields = isSelected;
  const isConditional = isConditionalSiblingItem(item);
  const showVisibilityGear = showRequiredToggle;

  const handleBlockClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      selection?.onSelect(item.linkId);
    },
    [selection, item.linkId],
  );

  const isGroup = item.type === 'group';
  const skipCard = isGroup || isRichText;
  const blockStyle = {
    position: 'relative' as const,
    transition: 'background-color 150ms, opacity 150ms, border-color 150ms',
    ...(hovered && { backgroundColor: 'var(--mantine-color-gray-1)' }),
    opacity: isDragging ? 0.4 : 1,
    borderLeft: !skipCard && isSelected
      ? '3px solid var(--mantine-color-gold-5)'
      : !skipCard && isConditional
        ? '2px dashed var(--mantine-color-gold-3)'
        : undefined,
    cursor: 'pointer',
  };

  const dragHandleEl = (
    <Box
      ref={(node: HTMLDivElement | null) => { drag(node); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'var(--mantine-spacing-lg)',
        marginTop: 'calc(var(--mantine-spacing-lg) * -1)',
        cursor: 'grab',
        opacity: hovered ? 0.6 : 0.25,
        transition: 'opacity 150ms',
      }}
    >
      <IconGripHorizontal size={14} color="var(--mantine-color-gray-5)" />
    </Box>
  );

  const actionRowEl = showExtraFields ? (
    <Group justify="space-between" wrap="wrap" gap="sm" mt="xs" style={{ minWidth: 0 }}>
      <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        {selection && (
          <BlockFooterActions
            parentLinkId={parentLinkId}
            parentIsPage={parentIsPage}
            index={index}
            onInsertAfter={selection.onInsertAfter}
          />
        )}
      </Box>
      <Group wrap="nowrap" gap="sm" style={{ flexShrink: 0 }}>
        {showVisibilityGear && (
          <ConditionalVisibilityGear
            item={item}
            siblings={siblingItems}
            siblingIndex={index}
            onUpdate={onUpdate}
          />
        )}
        <Tooltip label="Duplicate" withArrow position="top">
          <ActionIcon variant="subtle" color="gray" onClick={onDuplicate}>
            <IconCopy size={14} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Remove" withArrow position="top">
          <ActionIcon variant="subtle" color="red" onClick={onDelete}>
            <IconX size={14} />
          </ActionIcon>
        </Tooltip>
        <Switch
          label="Required"
          checked={!!item.required}
          disabled={!showRequiredToggle}
          onChange={(e) => showRequiredToggle && onUpdate({ required: e.currentTarget.checked })}
          styles={{ root: { alignItems: 'center' } }}
        />
        <Tooltip label={index > 0 ? 'Move up' : 'Move up (already first)'} withArrow position="top">
          <ActionIcon variant="subtle" color="gray" disabled={index === 0} onClick={onMoveUp}>
            <IconArrowUp size={14} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={index < total - 1 ? 'Move down' : 'Move down (already last)'} withArrow position="top">
          <ActionIcon variant="subtle" color="gray" disabled={index === total - 1} onClick={onMoveDown}>
            <IconArrowDown size={14} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Group>
  ) : null;

  const inner = (
    <Box style={{ minWidth: 0 }}>
      {/* Top: drag handle — only hover here darkens the block. For groups/pages, handle is inside BlockContent. */}
      {!isGroup && !isRichText && dragHandleEl}
      {isRichText && dragHandleEl}

      {/* Middle: block content. For groups/pages, action row is rendered inside BlockContent (Paper). */}
      <Box style={{ minWidth: 0 }}>
        <BlockContent
          item={item}
          onUpdate={onUpdate}
          onMoveBlock={onMoveBlock}
          selection={selection}
          parentLinkId={parentLinkId}
          siblingItems={siblingItems}
          siblingIndex={index}
          dragHandle={isGroup ? dragHandleEl : undefined}
          actionRow={isGroup ? actionRowEl : undefined}
          isSelected={isGroup ? isSelected : undefined}
          showExtraFields={!isGroup ? showExtraFields : undefined}
          hovered={isGroup ? hovered : undefined}
        />
      </Box>

      {/* Single row for non-group blocks (only when selected): Add buttons (left), Duplicate/Delete/Required/Move (right) */}
      {!isGroup && actionRowEl}
    </Box>
  );

  return (
    <Box ref={attachRef}>
      {showDropBefore && <Box h={3} mb={4} bg="gold.5" style={{ borderRadius: 2 }} />}
      {skipCard ? (
        <Box onClick={handleBlockClick} style={blockStyle}>
          {inner}
        </Box>
      ) : (
        <Paper withBorder radius="md" p="lg" onClick={handleBlockClick} style={blockStyle}>
          {inner}
        </Paper>
      )}
      {showDropAfter && <Box h={3} mt={4} bg="gold.5" style={{ borderRadius: 2 }} />}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// BlockContent – renders the right UI based on item type
// ---------------------------------------------------------------------------

interface BlockContentProps {
  item: QuestionnaireItem;
  onUpdate: (update: Partial<QuestionnaireItem>) => void;
  onMoveBlock: (linkId: string, parentLinkId: string | null, index: number) => void;
  selection?: SelectionContext;
  parentLinkId?: string | null;
  siblingItems?: QuestionnaireItem[];
  siblingIndex?: number;
  /** When set, render inside the block (used for group/page so drag handle is inside bordered Paper) */
  dragHandle?: ReactNode;
  /** When set, render inside group/page Paper so bottom row matches question blocks */
  actionRow?: ReactNode;
  /** When true, group/page Paper shows selection highlight (matches question block) */
  isSelected?: boolean;
  /** When true, show extra entry fields (options, validation, type selector); only for non-group blocks */
  showExtraFields?: boolean;
  /** When true, group/page Paper darkens (matches question block hover over drag handle) */
  hovered?: boolean;
}

function BlockContent({
  item,
  onUpdate,
  onMoveBlock,
  selection,
  parentLinkId = null,
  siblingIndex = 0,
  dragHandle,
  actionRow,
  isSelected,
  showExtraFields,
  hovered,
}: BlockContentProps): JSX.Element {
  // Group (section with children) — must be before display so page groups render as groups
  if (item.type === 'group') {
    return (
      <GroupBlock
        item={item}
        onUpdate={onUpdate}
        onMoveBlock={onMoveBlock}
        selection={selection}
        dragHandle={dragHandle}
        actionRow={actionRow}
        isSelected={isSelected}
        hovered={hovered}
      />
    );
  }

  // Rich text block: Tiptap inline editor for long-form document content
  if (isRichTextBlock(item)) {
    return <RichTextBlock item={item} onUpdate={onUpdate} showExtraFields={showExtraFields} />;
  }

  // Display types: show type selector when selected, plus content
  if (item.type === 'display') {
    return <DisplayBlock item={item} onUpdate={onUpdate} showExtraFields={showExtraFields} />;
  }

  // All question types: show field preview and options only when selected
  return (
    <QuestionBlock
      item={item}
      onUpdate={onUpdate}
      showExtraFields={showExtraFields}
      siblingIndex={siblingIndex}
      parentLinkId={parentLinkId}
      selection={selection}
    />
  );
}

// ---------------------------------------------------------------------------
// RichTextBlock – inline Tiptap editor for long-form document content
// ---------------------------------------------------------------------------

function RichTextBlock({
  item,
  onUpdate,
}: {
  item: QuestionnaireItem;
  onUpdate: (u: Partial<QuestionnaireItem>) => void;
  showExtraFields?: boolean;
}): JSX.Element {
  const initialContent = item.initial?.[0]?.valueString ?? '';
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commitHtml = useCallback(
    (html: string) => {
      onUpdate({ initial: [{ valueString: html }] });
    },
    [onUpdate],
  );

  const scheduleCommit = useCallback(
    (html: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        commitHtml(html);
      }, 400);
    },
    [commitHtml],
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapUnderline,
      TiptapLink,
      Placeholder.configure({ placeholder: 'Enter document content…' }),
    ],
    content: initialContent,
    onUpdate: ({ editor: e }) => {
      scheduleCommit(e.getHTML());
    },
    onBlur: ({ editor: e }) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      commitHtml(e.getHTML());
    },
  });

  const handleInsertVariable = useCallback(
    (token: string) => {
      if (!editor) return;
      editor.chain().focus().insertContent(token).run();
    },
    [editor],
  );

  return (
    <RichTextEditor editor={editor} styles={{ root: { backgroundColor: 'var(--mantine-color-white)' } }}>
      <RichTextEditor.Toolbar sticky stickyOffset={0}>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
          <RichTextEditor.Strikethrough />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H1 />
          <RichTextEditor.H2 />
          <RichTextEditor.H3 />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Link />
          <RichTextEditor.Unlink />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Hr />
        </RichTextEditor.ControlsGroup>
        <RichTextEditor.ControlsGroup>
          <VariablePickerPopover onPick={handleInsertVariable} triggerLabel="Insert variable" />
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>
      <RichTextEditor.Content />
    </RichTextEditor>
  );
}

// ---------------------------------------------------------------------------
// DisplayBlock – display item with type selector (so type can be changed away from display)
// ---------------------------------------------------------------------------

function DisplayBlock({
  item,
  onUpdate,
  showExtraFields = false,
}: {
  item: QuestionnaireItem;
  onUpdate: (u: Partial<QuestionnaireItem>) => void;
  showExtraFields?: boolean;
}): JSX.Element {
  const effectiveType = getEffectiveType(item);
  const handleTypeChange = useCallback(
    (newType: string | null) => {
      if (!newType || newType === effectiveType) return;
      const converted = convertItemType(item, newType);
      onUpdate(converted);
    },
    [item, onUpdate, effectiveType],
  );

  const displayPlaceholder = isHeading(item)
    ? 'Section heading'
    : isDividerItem(item)
      ? ''
      : 'Description text';

  return (
    <Stack gap="sm">
      <Group gap="sm" align="flex-start" wrap="nowrap">
        {item.required && (
          <Text c="red" fw={700} size="sm" mt={6}>
            *
          </Text>
        )}
        <Box style={{ flex: 1, minWidth: 0, width: '100%' }}>
          <VariableTextarea
            value={item.text ?? ''}
            onChange={(next) => onUpdate({ text: next })}
            deferredCommit
            variant="unstyled"
            autosize
            minRows={1}
            styles={{
              input: {
                ...INNER_INPUT_BG,
                fontSize: 'var(--mantine-font-size-sm)',
                fontWeight: 500,
                padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
              },
            }}
            placeholder={displayPlaceholder || 'Question text'}
          />
        </Box>
        {showExtraFields && (
          <Select
            leftSection={getIconForQuestionType(effectiveType)}
            style={{ width: 180, flexShrink: 0 }}
            styles={{ input: INNER_INPUT_BG }}
            value={effectiveType}
            onChange={handleTypeChange}
            data={QUESTION_TYPES.map((t) => ({ value: t.value, label: t.label }))}
            allowDeselect={false}
          />
        )}
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// GroupBlock – section heading + recursive children + inline add controls
// ---------------------------------------------------------------------------

function GroupBlock({
  item,
  onUpdate,
  onMoveBlock,
  selection,
  dragHandle,
  actionRow,
  isSelected,
  hovered,
}: BlockContentProps): JSX.Element {
  const children = item.item ?? [];

  const updateChild = useCallback(
    (childIndex: number, update: Partial<QuestionnaireItem>) => {
      const newChildren = [...children];
      newChildren[childIndex] = applyQuestionnaireItemUpdate(newChildren[childIndex], update);
      onUpdate({ item: newChildren });
    },
    [children, onUpdate],
  );

  const removeChild = useCallback(
    (childIndex: number) => {
      const { next, clearedCount } = removeSiblingWithConditionalCleanup(children, childIndex);
      onUpdate({ item: next });
      notifyConditionalRulesCleared(clearedCount);
    },
    [children, onUpdate],
  );

  const duplicateChild = useCallback(
    (childIndex: number) => {
      const clone = cloneItemForDuplicate(children[childIndex]);
      const next = [
        ...children.slice(0, childIndex + 1),
        clone,
        ...children.slice(childIndex + 1),
      ];
      onUpdate({ item: next });
    },
    [children, onUpdate],
  );

  const moveChild = useCallback(
    (childIndex: number, direction: 'up' | 'down') => {
      const delta = direction === 'up' ? -1 : 1;
      onUpdate({ item: reorderItems(children, childIndex, delta) });
    },
    [children, onUpdate],
  );

  const handleAddItem = useCallback(() => {
    const newItem = createQuestionItem();
    onUpdate({ item: [...children, newItem] });
    selection?.onSelect(newItem.linkId);
  }, [children, onUpdate, selection]);

  const isPage = getItemControl(item) === 'page';
  const groupDropRef = useRef<HTMLDivElement | null>(null);
  /** For page blocks: reject when cursor is in top/bottom margin so the outer EditableBlock can receive "before" / "after" page. */
  const PAGE_ANCHOR_MARGIN = 0.25;
  const [{ isGroupDrop }, groupDrop] = useDrop<DragItem, void, { isGroupDrop: boolean }>({
    accept: DRAG_TYPE,
    drop: (dragItem, monitor) => {
      if (monitor.didDrop()) return;
      if (dragItem.linkId === item.linkId) return;
      onMoveBlock(dragItem.linkId, item.linkId, children.length);
    },
    canDrop: (dragItem, monitor) => {
      if (dragItem.linkId === item.linkId || (dragItem.isPage && isPage)) return false;
      if (dragItem.parentLinkId === item.linkId) return false;
      if (isPage && groupDropRef.current) {
        const rect = groupDropRef.current.getBoundingClientRect();
        const offset = monitor.getClientOffset();
        if (offset) {
          const yFrac = (offset.y - rect.top) / rect.height;
          if (yFrac < PAGE_ANCHOR_MARGIN || yFrac > 1 - PAGE_ANCHOR_MARGIN) return false;
        }
      }
      return true;
    },
    collect: (monitor) => ({
      isGroupDrop: monitor.isOver({ shallow: true }) && monitor.canDrop(),
    }),
  });

  const gold = 'var(--mantine-color-gold-5)';
  const defaultBorder = 'var(--mantine-color-default-border)';
  const borderStyles = {
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: isSelected ? 3 : 1,
    borderTopStyle: 'solid' as const,
    borderRightStyle: 'solid' as const,
    borderBottomStyle: 'solid' as const,
    borderLeftStyle: 'solid' as const,
    borderTopColor: isGroupDrop ? gold : defaultBorder,
    borderRightColor: isGroupDrop ? gold : defaultBorder,
    borderBottomColor: isGroupDrop ? gold : defaultBorder,
    borderLeftColor: isSelected || isGroupDrop ? gold : defaultBorder,
  };

  return (
    <Paper
      withBorder
      radius="md"
      ref={(node: HTMLDivElement | null) => {
        groupDropRef.current = node;
        groupDrop(node);
      }}
      style={{
        ...borderStyles,
        padding: 'var(--mantine-spacing-lg)',
        ...((isGroupDrop || hovered) && {
          backgroundColor: isGroupDrop ? 'var(--mantine-color-gold-0)' : 'var(--mantine-color-gray-1)',
        }),
        transition: 'border-color 150ms, background-color 150ms',
      }}
    >
      {dragHandle}
      <VariableTextInput
        value={item.text ?? ''}
        onChange={(next) => onUpdate({ text: next })}
        deferredCommit
        variant="unstyled"
        styles={{
          input: {
            ...INNER_INPUT_BG,
            fontWeight: 600,
            color: 'var(--mantine-primary-color-filled)',
            fontSize: 'var(--mantine-font-size-md)',
            paddingLeft: 'var(--mantine-spacing-md)',
          },
        }}
        placeholder={getItemControl(item) === 'page' ? 'New Page' : 'Section title'}
      />

      <Stack gap="md" mt="md">
        {children.map((child, childIndex) => (
          <EditableBlock
            key={child.id || child.linkId}
            item={child}
            index={childIndex}
            total={children.length}
            parentLinkId={item.linkId}
            parentIsPage={isPage}
            siblingItems={children}
            onUpdate={(update) => updateChild(childIndex, update)}
            onDuplicate={() => duplicateChild(childIndex)}
            onDelete={() => removeChild(childIndex)}
            onMoveUp={() => moveChild(childIndex, 'up')}
            onMoveDown={() => moveChild(childIndex, 'down')}
            onMoveBlock={onMoveBlock}
            selection={selection}
          />
        ))}
      </Stack>

      {/* Insert controls inside page/section — only when this group is selected */}
      {isSelected && (
        <Group gap="xs" mt="sm">
          <Button
            variant="subtle"
            size="compact-xs"
            leftSection={<IconPlus size={12} />}
            onClick={handleAddItem}
          >
            Insert question
          </Button>
        </Group>
      )}
      {actionRow}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// QuestionBlock – editable label + type select + field type preview
// ---------------------------------------------------------------------------

function QuestionBlock({
  item,
  onUpdate,
  showExtraFields = false,
  siblingIndex = 0,
  parentLinkId = null,
  selection,
}: Pick<
  BlockContentProps,
  'item' | 'onUpdate' | 'showExtraFields' | 'siblingIndex' | 'parentLinkId' | 'selection'
>): JSX.Element {
  const effectiveType = getEffectiveType(item);
  const handleTypeChange = useCallback(
    (newType: string | null) => {
      if (!newType || newType === effectiveType) return;
      const converted = convertItemType(item, newType);
      onUpdate(converted);
    },
    [item, onUpdate, effectiveType],
  );

  const handleAddFollowUpQuestion = useCallback(
    (optionIndex: number) => {
      if (!selection) return;
      const partial = createConditionalFollowUpItem(item, optionIndex);
      const newItem = {
        ...partial,
        id: generateId(),
        linkId: generateLinkId('q'),
      } as QuestionnaireItem;
      selection.onInsertAfter(parentLinkId, siblingIndex, newItem);
    },
    [item, parentLinkId, selection, siblingIndex],
  );

  return (
    <Stack gap="sm">
      <Group gap="sm" align="flex-start" wrap="nowrap">
        {item.required && (
          <Text c="red" fw={700} size="sm" mt={6}>
            *
          </Text>
        )}
        <Box style={{ flex: 1, minWidth: 0, width: '100%' }}>
          <VariableTextarea
            value={item.text ?? ''}
            onChange={(next) => onUpdate({ text: next })}
            deferredCommit
            variant="unstyled"
            autosize
            minRows={1}
            styles={{
              input: {
                ...INNER_INPUT_BG,
                fontSize: 'var(--mantine-font-size-sm)',
                fontWeight: 500,
                padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
              },
            }}
            placeholder="Question text"
          />
        </Box>
        {showExtraFields && (
          <Select
            leftSection={getIconForQuestionType(effectiveType)}
            style={{ width: 180, flexShrink: 0 }}
            styles={{ input: INNER_INPUT_BG }}
            value={effectiveType}
            onChange={handleTypeChange}
            data={QUESTION_TYPES.map((t) => ({ value: t.value, label: t.label }))}
            allowDeselect={false}
          />
        )}
        {showExtraFields && effectiveType === 'multi-choice' && (
          <Select
            aria-label="Multi choice layout"
            style={{ width: 150, flexShrink: 0 }}
            styles={{ input: INNER_INPUT_BG }}
            value={getMultiChoiceLayout(item)}
            onChange={(layout) => layout && onUpdate({ extension: withItemControl(item, layout) })}
            data={MULTI_CHOICE_LAYOUTS.map((l) => ({ value: l.value, label: l.label }))}
            allowDeselect={false}
          />
        )}
        {showExtraFields && effectiveType === 'choice' && (
          <Select
            aria-label="Single choice layout"
            style={{ width: 150, flexShrink: 0 }}
            styles={{ input: INNER_INPUT_BG }}
            value={getSingleChoiceLayout(item)}
            onChange={(layout) =>
              layout && onUpdate({ extension: singleChoiceLayoutExtensions(item, layout) })
            }
            data={SINGLE_CHOICE_LAYOUTS.map((l) => ({ value: l.value, label: l.label }))}
            allowDeselect={false}
          />
        )}
      </Group>

      {/* Always show field preview (preview-only when not selected, so unselected looks like final form) */}
      <FieldPreview
        item={item}
        onUpdate={onUpdate}
        previewOnly={!showExtraFields}
        onAddFollowUpQuestion={
          showExtraFields && !item.repeats ? handleAddFollowUpQuestion : undefined
        }
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// FieldPreview – visual representation of the answer field
// ---------------------------------------------------------------------------

interface FieldPreviewProps {
  item: QuestionnaireItem;
  onUpdate: (update: Partial<QuestionnaireItem>) => void;
  /** When true, show preview-only (no option editing, no Add option); used for unselected blocks */
  previewOnly?: boolean;
  /** Insert a sibling follow-up question after this choice block. */
  onAddFollowUpQuestion?: (optionIndex: number) => void;
}

function FieldPreview({
  item,
  onUpdate,
  previewOnly = false,
  onAddFollowUpQuestion,
}: FieldPreviewProps): JSX.Element {
  // Short text / string
  if (item.type === 'string') {
    return (
      <TextInput
        disabled
        styles={{ input: INNER_INPUT_BG }}
      />
    );
  }

  // Integer / Decimal
  if (item.type === 'integer' || item.type === 'decimal') {
    return (
      <NumberInput
        disabled
        styles={{ input: INNER_INPUT_BG }}
      />
    );
  }

  // URL
  if (item.type === 'url') {
    return (
      <TextInput
        disabled
        styles={{ input: INNER_INPUT_BG }}
      />
    );
  }

  // Long text
  if (item.type === 'text') {
    return (
      <Textarea disabled styles={{ input: INNER_INPUT_BG }} minRows={3} />
    );
  }

  // Date
  if (item.type === 'date') {
    return (
      <DateInput
        disabled
        leftSection={<IconCalendarEvent size={16} />}
        styles={{ input: INNER_INPUT_BG }}
      />
    );
  }

  // DateTime
  if (item.type === 'dateTime') {
    return (
      <DateTimePicker
        disabled
        leftSection={<IconCalendarEvent size={16} />}
        styles={{ input: INNER_INPUT_BG }}
      />
    );
  }

  // Time
  if (item.type === 'time') {
    return (
      <TimePicker
        disabled
        format="12h"
        leftSection={<IconClock size={16} />}
        styles={{ input: INNER_INPUT_BG }}
      />
    );
  }

  // Boolean
  if (item.type === 'boolean') {
    return <Checkbox disabled label="Yes / No" size="sm" styles={{ body: INNER_INPUT_BG }} />;
  }

  // Attachment
  if (item.type === 'attachment') {
    return (
      <Box
        style={{
          display: 'block',
          padding: 'var(--mantine-spacing-md)',
          borderRadius: 'var(--mantine-radius-md)',
          border: '2px dashed var(--mantine-color-gray-4)',
          backgroundColor: 'var(--mantine-color-white)',
          textAlign: 'center',
          opacity: 0.7,
        }}
      >
        <Stack gap={4} align="center">
          <IconUpload size={24} style={{ color: 'var(--mantine-color-gray-5)' }} />
          <Text size="xs" c="dimmed">File attachment</Text>
        </Stack>
      </Box>
    );
  }

  // Quantity — value + unit side by side
  if (item.type === 'quantity') {
    return (
      <Group gap="sm" grow>
        <NumberInput disabled styles={{ input: INNER_INPUT_BG }} />
        <TextInput disabled styles={{ input: INNER_INPUT_BG }} />
      </Group>
    );
  }

  // Choice / open-choice with repeats (checkboxes)
  if (isChoiceQuestion(item) && item.repeats) {
    return (
      <ChoiceOptionsEditor
        item={item}
        onUpdate={onUpdate}
        multi
        previewOnly={previewOnly}
        isOpenChoice={item.type === 'open-choice'}
        onAddFollowUpQuestion={onAddFollowUpQuestion}
      />
    );
  }

  // Choice / open-choice (dropdown)
  if (isDropdown(item)) {
    return (
      <ChoiceOptionsEditor
        item={item}
        onUpdate={onUpdate}
        dropdown
        previewOnly={previewOnly}
        isOpenChoice={item.type === 'open-choice'}
        onAddFollowUpQuestion={onAddFollowUpQuestion}
      />
    );
  }

  // Choice / open-choice (single-select radio)
  if (isChoiceQuestion(item)) {
    return (
      <ChoiceOptionsEditor
        item={item}
        onUpdate={onUpdate}
        previewOnly={previewOnly}
        isOpenChoice={item.type === 'open-choice'}
        onAddFollowUpQuestion={onAddFollowUpQuestion}
      />
    );
  }

  // Fallback
  return <TextInput disabled styles={{ input: INNER_INPUT_BG }} />;
}

// ---------------------------------------------------------------------------
// ChoiceOptionsEditor – edit answer options inline
// ---------------------------------------------------------------------------

interface ChoiceOptionsEditorProps {
  item: QuestionnaireItem;
  onUpdate: (update: Partial<QuestionnaireItem>) => void;
  multi?: boolean;
  dropdown?: boolean;
  /** When true, show options as read-only preview (no editing, no Add option) */
  previewOnly?: boolean;
  /** When true, show an "Other" free-text hint (open-choice type) */
  isOpenChoice?: boolean;
  /** Insert a sibling follow-up question conditioned on this option. */
  onAddFollowUpQuestion?: (optionIndex: number) => void;
}

/** Local draft while typing; commits to questionnaire state on blur to avoid full-tree re-renders per keystroke. */
function OptionAddonPlaceholderInput({
  value,
  onCommit,
}: {
  value: string;
  onCommit: (value: string) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const commit = useCallback(() => {
    if (draft !== value) {
      onCommit(draft);
    }
  }, [draft, onCommit, value]);

  return (
    <Textarea
      value={draft}
      onChange={(e) => setDraft(e.currentTarget.value)}
      onBlur={commit}
      autosize
      minRows={2}
      maxRows={6}
      size="xs"
      styles={{ input: INNER_INPUT_BG }}
    />
  );
}

function ChoiceOptionsEditor({
  item,
  onUpdate,
  multi = false,
  dropdown = false,
  previewOnly = false,
  isOpenChoice = false,
  onAddFollowUpQuestion,
}: ChoiceOptionsEditorProps): JSX.Element {
  const options: QuestionnaireItemAnswerOption[] =
    item.answerOption ?? [];
  const showAddonControls = supportsPerOptionAddons(item);

  const applyParentUpdate = useCallback(
    (next: QuestionnaireItem) => {
      const synced = syncOptionAddonEnableWhen(next);
      onUpdate({
        answerOption: synced.answerOption,
        item: synced.item,
      });
    },
    [onUpdate],
  );

  const updateOption = useCallback(
    (optIdx: number, value: string) => {
      const newOptions = [...options];
      newOptions[optIdx] = patchAnswerOptionDisplayLabel(newOptions[optIdx], value);
      applyParentUpdate({ ...item, answerOption: newOptions });
    },
    [applyParentUpdate, item, options],
  );

  const removeOption = useCallback(
    (optIdx: number) => {
      const next = removeOptionAddon(
        { ...item, answerOption: options.filter((_, i) => i !== optIdx) },
        optIdx,
      );
      applyParentUpdate(next);
    },
    [applyParentUpdate, item, options],
  );

  const addOption = useCallback(() => {
    applyParentUpdate({
      ...item,
      answerOption: [
        ...options,
        { id: generateId(), valueString: `Option ${options.length + 1}` },
      ],
    });
  }, [applyParentUpdate, item, options]);

  const toggleOptionAddon = useCallback(
    (optIdx: number) => {
      const hasAddon = getOptionAddonPlaceholder(item, optIdx) !== null;
      const synced = syncOptionAddonEnableWhen(
        hasAddon ? removeOptionAddon(item, optIdx) : setOptionAddonPlaceholder(item, optIdx, ''),
      );
      onUpdate({ item: synced.item });
    },
    [item, onUpdate],
  );

  const updateOptionAddonPlaceholder = useCallback(
    (optIdx: number, placeholder: string) => {
      onUpdate({ item: setOptionAddonPlaceholder(item, optIdx, placeholder).item });
    },
    [item, onUpdate],
  );

  const renderOptionAddonIcon = (optIdx: number) => {
    if (!showAddonControls) return null;
    const hasAddon = getOptionAddonPlaceholder(item, optIdx) !== null;

    return (
      <Tooltip
        label={hasAddon ? 'Remove follow-up input' : 'Add follow-up input for this option'}
        withArrow
        position="top"
      >
        <ActionIcon
          variant={hasAddon ? 'light' : 'subtle'}
          color={hasAddon ? 'gold' : 'gray'}
          size="xs"
          onClick={() => toggleOptionAddon(optIdx)}
        >
          <IconMessagePlus size={12} />
        </ActionIcon>
      </Tooltip>
    );
  };

  const renderSiblingFollowUpIcon = (optIdx: number) => {
    if (!onAddFollowUpQuestion || multi) return null;
    return (
      <Tooltip label="Add follow-up question for this option" withArrow position="top">
        <ActionIcon
          variant="subtle"
          color="gray"
          size="xs"
          onClick={() => onAddFollowUpQuestion(optIdx)}
        >
          <IconRoute size={12} />
        </ActionIcon>
      </Tooltip>
    );
  };

  const renderOptionAddonInput = (optIdx: number) => {
    if (!showAddonControls) return null;
    const placeholder = getOptionAddonPlaceholder(item, optIdx);
    if (placeholder === null) return null;

    return (
      <Box pl={multi || dropdown ? 28 : 36} style={{ width: '100%', minWidth: 0 }}>
        <OptionAddonPlaceholderInput
          value={placeholder}
          onCommit={(next) => updateOptionAddonPlaceholder(optIdx, next)}
        />
      </Box>
    );
  };

  const renderPreviewAddonHint = (optIdx: number) => {
    const placeholder = getOptionAddonPlaceholder(item, optIdx);
    if (!placeholder?.trim()) return null;
    return (
      <Text size="xs" c="dimmed" fs="italic" pl={28} style={{ wordBreak: 'break-word' }}>
        + follow-up: {placeholder}
      </Text>
    );
  };

  if (previewOnly) {
    if (dropdown) {
      return (
        <Stack gap="xs">
          {options.map((opt, i) => (
            <Stack key={opt.id || i} gap={4}>
              <Group gap="xs" wrap="nowrap">
                <Text size="xs" c="dimmed" w={20} ta="center">
                  {i + 1}.
                </Text>
                <Text size="sm" c="dimmed">
                  {getAnswerOptionDisplayLabel(opt) || `Option ${i + 1}`}
                </Text>
              </Group>
              {renderPreviewAddonHint(i)}
            </Stack>
          ))}
          {isOpenChoice && (
            <Text size="xs" c="dimmed" fs="italic" mt={2}>+ free-text &ldquo;Other&rdquo; option</Text>
          )}
        </Stack>
      );
    }
    return (
      <Stack gap="xs">
        {options.map((opt, i) => (
          <Stack key={opt.id || i} gap={4}>
            <Group gap="xs" wrap="nowrap">
              {multi ? (
                <Checkbox disabled size="sm" styles={{ body: INNER_INPUT_BG }} />
              ) : (
                <Radio disabled size="sm" value="" styles={{ body: INNER_INPUT_BG }} />
              )}
              <Text size="sm" c="dimmed">
                {getAnswerOptionDisplayLabel(opt) || `Option ${i + 1}`}
              </Text>
            </Group>
            {renderPreviewAddonHint(i)}
          </Stack>
        ))}
        {isOpenChoice && (
          <Group gap="xs" wrap="nowrap">
            {multi ? (
              <Checkbox disabled size="sm" styles={{ body: INNER_INPUT_BG }} />
            ) : (
              <Radio disabled size="sm" value="" styles={{ body: INNER_INPUT_BG }} />
            )}
            <Text size="sm" c="dimmed" fs="italic">Other (specify)</Text>
          </Group>
        )}
      </Stack>
    );
  }

  if (dropdown) {
    return (
      <Stack gap="sm">
        {options.map((opt, i) => (
          <Stack key={opt.id || i} gap={4}>
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <Text size="xs" c="dimmed" w={20} ta="center" mt={8}>
                {i + 1}.
              </Text>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <VariableTextInput
                  value={getAnswerOptionDisplayLabel(opt)}
                  onChange={(next) => updateOption(i, next)}
                  deferredCommit
                  variant="unstyled"
                  size="sm"
                  placeholder={`Option ${i + 1}`}
                  styles={{
                    input: { ...INNER_INPUT_BG, padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)' },
                  }}
                />
              </Box>
              {renderOptionAddonIcon(i)}
              {renderSiblingFollowUpIcon(i)}
              {options.length > 1 && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="xs"
                  onClick={() => removeOption(i)}
                >
                  <IconX size={12} />
                </ActionIcon>
              )}
            </Group>
            {renderOptionAddonInput(i)}
          </Stack>
        ))}
        {isOpenChoice && (
          <Text size="xs" c="dimmed" fs="italic" mt={2}>+ free-text &ldquo;Other&rdquo; option</Text>
        )}
        <Button
          variant="subtle"
          size="compact-xs"
          leftSection={<IconPlus size={12} />}
          onClick={addOption}
          w="fit-content"
        >
          Add option
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap="sm">
      {options.map((opt, i) => (
        <Stack key={opt.id || i} gap={4}>
          <Group gap="xs" wrap="nowrap" align="flex-start">
            {multi ? (
              <Checkbox disabled size="sm" styles={{ body: INNER_INPUT_BG }} />
            ) : (
              <Radio disabled size="sm" value="" styles={{ body: INNER_INPUT_BG }} />
            )}
            <Box style={{ flex: 1, minWidth: 0 }}>
              <VariableTextInput
                value={getAnswerOptionDisplayLabel(opt)}
                onChange={(next) => updateOption(i, next)}
                deferredCommit
                variant="unstyled"
                size="sm"
                placeholder={`Option ${i + 1}`}
                styles={{
                  input: { ...INNER_INPUT_BG, padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)' },
                }}
              />
            </Box>
            {renderOptionAddonIcon(i)}
            {renderSiblingFollowUpIcon(i)}
            {options.length > 1 && (
              <ActionIcon
                variant="subtle"
                color="gray"
                size="xs"
                onClick={() => removeOption(i)}
              >
                <IconX size={12} />
              </ActionIcon>
            )}
          </Group>
          {renderOptionAddonInput(i)}
        </Stack>
      ))}
      {isOpenChoice && (
        <Group gap="xs" wrap="nowrap">
          {multi ? (
            <Checkbox disabled size="sm" styles={{ body: INNER_INPUT_BG }} />
          ) : (
            <Radio disabled size="sm" value="" styles={{ body: INNER_INPUT_BG }} />
          )}
          <Text size="sm" c="dimmed" fs="italic">Other (specify)</Text>
        </Group>
      )}
      <Button
        variant="subtle"
        size="compact-xs"
        leftSection={<IconPlus size={12} />}
        onClick={addOption}
        w="fit-content"
      >
        Add option
      </Button>
    </Stack>
  );
}
